// DOS-6: split the pen list into active (shown by default) and inactive pens
// (empty / expired / retired) grouped by status for the expandable section.
// Each entry keeps the pen's ORIGINAL index in the source array, because the
// Log Dose page selects pens by their index in `pens`.

export interface IndexedPen<T> {
  pen: T;
  index: number;
}

export interface PenGroup<T> {
  status: string;
  items: IndexedPen<T>[];
}

// Order inactive groups are displayed in.
export const INACTIVE_STATUS_ORDER = ['empty', 'expired', 'retired'] as const;

export const STATUS_LABELS: Record<string, string> = {
  empty: 'Empty',
  expired: 'Expired',
  retired: 'Retired',
};

export function partitionPensByStatus<T extends { status?: string }>(pens: T[]): {
  active: IndexedPen<T>[];
  inactive: IndexedPen<T>[];
  groups: PenGroup<T>[];
} {
  const indexed = pens.map((pen, index) => ({ pen, index }));
  const active = indexed.filter((x) => x.pen.status === 'active');
  const inactive = indexed.filter((x) => x.pen.status !== 'active');

  const groups = INACTIVE_STATUS_ORDER
    .map((status) => ({
      status,
      // Treat a missing status as 'empty' so no pen is ever dropped from the list.
      items: inactive.filter((x) => (x.pen.status || 'empty') === status),
    }))
    .filter((g) => g.items.length > 0);

  return { active, inactive, groups };
}
